import React from 'react';
import Layout from '../components/Layout';
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "../components/ui/carousel";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import AutoHeight from 'embla-carousel-auto-height';
import Autoplay from 'embla-carousel-autoplay';
import { toast } from "sonner";
import { 
  TrendingUp, 
  Users, 
  Lightbulb, 
  Search, 
  DollarSign, 
  Globe, 
  Cpu, 
  GraduationCap, 
  Briefcase,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';

import heroTeam from "../assets/hero_team.jpg";
import uiHome from "../assets/ui_home.png";
import uiOffers from "../assets/ui_offers.png";
import uiDashboard from "../assets/ui_dashboard.png";
import uiCompanies from "../assets/ui_companies.png";
import mapWorld from "../assets/map_world.png";
import flagKorea from "../assets/flag_korea.png";
import flagMalaysia from "../assets/flag_malaysia.png";
import flagSingapore from "../assets/flag_singapore.png";
import flagChina from "../assets/flag_china.png";
import thomasPortrait from "../assets/thomas_portrait.jpg";
import thomasFushimi from "../assets/thomas_fushimi.jpg";
import thomasExhibition from "../assets/thomas_exhibition.jpg";
import gregoireJapan from "../assets/gregoire_japan.jpg";
import diegoTokyo from "../assets/diego_tokyo.png";
import yoannJapan from "../assets/yoann_japan.jpg";
import mirabelleJapan from "../assets/mirabelle_japan.jpg";

const InterfaceItem = ({ item, i }) => {
  return (
    <div className={`flex flex-col lg:flex-row gap-12 items-center ${i % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
      <div className="w-full lg:w-1/2">
        <div className="rounded-xl overflow-hidden shadow-2xl border border-slate-200 bg-white group hover:shadow-3xl transition-all duration-500">
          <img 
            src={item.img} 
            alt={item.title} 
            className="w-full h-auto object-cover transform group-hover:scale-105 transition-transform duration-700" 
          />
        </div>
      </div>
      <div className="w-full lg:w-1/2 space-y-4">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 text-red-600 font-bold text-lg mb-2">
          {i + 1}
        </div>
        <h3 className="text-3xl font-bold text-slate-900">{item.title}</h3>
        <p className="text-xl text-slate-600 leading-relaxed">{item.text}</p>
      </div>
    </div>
  );
};

const Home = () => {
  const handleDemoClick = (action) => {
    toast.info(`${action} functionality is coming soon!`, {
      description: "This is a showcase demo site."
    });
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-20 pb-32 overflow-hidden bg-slate-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 z-10">
              <div className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm font-medium text-slate-600">
                <span className="relative flex h-2 w-2 mr-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-slate-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-slate-500"></span>
                </span>
                Pre-seed Startup
              </div>
              <h1 className="text-5xl md:text-6xl font-bold tracking-tight text-slate-900 leading-tight">
                Recruit Top <span className="text-red-600">Engineering</span>, <span className="text-red-600">Business</span>, <span className="text-red-600">Sales</span>, <span className="text-red-600">Marketing</span>, <span className="text-red-600">Tourism</span> & <span className="text-red-600">Hospitality</span> Interns from France
              </h1>
              <p className="text-xl text-slate-600 max-w-lg leading-relaxed">
                Access an exclusive pool of students from France's top Universities. Find qualified, motivated, and pre-vetted candidates ready to join your team.
              </p>
              <div className="flex flex-col gap-3 pt-2">
                <p className="text-lg font-medium text-slate-700 italic">
                  "Empowering Interns. Elevating Employers."
                </p>
              </div>
            </div>
            <div className="relative lg:h-[600px] w-full rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src={heroTeam} 
                alt="Diverse team collaborating in Tokyo office" 
                className="object-cover w-full h-full transform hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent"></div>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem / Data-Driven Hook */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Solving Japan's Critical Talent Shortage</h2>
            <p className="text-lg text-slate-600">
              Traditional recruitment methods for experienced hires are no longer enough. Internjin opens the door to France's highly skilled, globally-minded student talent.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border-none shadow-lg bg-slate-50">
              <CardContent className="p-8 flex flex-col items-center text-center">
                <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                  <TrendingUp className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">1.28 Job-to-Applicant Ratio</h3>
                <p className="text-slate-600">
                  Japan's job-to-applicant ratio remains high, making competition for skilled local talent fierce.
                </p>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg bg-slate-50">
              <CardContent className="p-8 flex flex-col items-center text-center">
                <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                  <Users className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">790,000 IT Shortfall</h3>
                <p className="text-slate-600">
                  METI projects a shortfall of up to 790,000 IT personnel by 2030. Your company's growth depends on finding new talent pipelines.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* The Intern Advantage */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Focus on Interns? <br/><span className="text-red-500">A Smarter Strategy for Growth.</span></h2>
            <p className="text-lg text-slate-300 max-w-2xl">
              In a market where experienced candidates are scarce and expensive, top-tier student talent offers a more flexible, cost-effective, and powerful solution.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <GraduationCap className="h-6 w-6 text-red-400" />,
                title: "Shape Future Leaders",
                desc: "Train them in your company's specific methods and culture from day one."
              },
              {
                icon: <Lightbulb className="h-6 w-6 text-red-400" />,
                title: "Inject Innovation",
                desc: "Students bring the latest knowledge in AI, Data Science, and global business models."
              },
              {
                icon: <Search className="h-6 w-6 text-red-400" />,
                title: "De-Risk Hiring",
                desc: "Evaluate skills and cultural fit before committing to a full-time offer."
              },
              {
                icon: <DollarSign className="h-6 w-6 text-red-400" />,
                title: "Cost-Effective",
                desc: "Secure high-level skills for specific projects without the high cost of senior hires."
              }
            ].map((item, index) => (
              <div key={index} className="bg-slate-800/50 p-6 rounded-xl border border-slate-700 hover:border-red-500/50 transition-colors">
                <div className="h-12 w-12 bg-slate-900 rounded-lg flex items-center justify-center mb-4 border border-slate-700">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Solution */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge variant="outline" className="mb-4 border-red-200 text-red-700 bg-red-50">Our Solution</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">The Only Recruitment Platform Connecting France and Japan</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {[
              {
                title: "Rare Talent Access",
                desc: "Access 10,000+ students from France's top University (Business, Engineering, Sales, Marketing, Tourism & Hospitality).",
                icon: <Globe className="h-6 w-6 text-white" />
              },
              {
                title: "AI Smart Matching",
                desc: "Instantly receive a shortlist of candidates who perfectly match your criteria.",
                icon: <Cpu className="h-6 w-6 text-white" />
              },
              {
                title: "Cultural Onboarding",
                desc: "We don't just find talent; we prepare them. All candidates complete our 'Working in Japan' program.",
                icon: <Briefcase className="h-6 w-6 text-white" />
              },
              {
                title: "Employer Branding",
                desc: "Showcase your company's unique culture with a customized employer page to attract candidates.",
                icon: <Users className="h-6 w-6 text-white" />
              }
            ].map((feature, i) => (
              <div key={i} className="flex gap-6 items-start group">
                <div className="flex-shrink-0 h-14 w-14 rounded-2xl bg-red-600 flex items-center justify-center shadow-lg shadow-red-600/20 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Website Interface Overview */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Website Interface Overview</h2>
            <p className="text-lg text-slate-600">
              Experience a modern, streamlined platform designed for efficiency.
            </p>
          </div>

          <div className="space-y-24">
            {[
              {
                img: uiHome,
                text: "An intuitive, user-centric homepage interface.",
                title: "Home Page"
              },
              {
                img: uiOffers,
                text: "A dynamic job board featuring curated opportunities and frictionless navigation.",
                title: "Job Board"
              },
              {
                img: uiDashboard,
                text: "A comprehensive backend interface for seamless talent acquisition management.",
                title: "Recruiter Dashboard"
              },
              {
                img: uiCompanies,
                text: "Top Employers: Explore the industry leaders hiring now.",
                title: "Company Directory"
              }
            ].map((item, i) => (
              <InterfaceItem key={i} item={item} i={i} />
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-16">Successful Student Hiring in 4 Easy Steps</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative">
            {/* Connecting Line (Desktop) */}
            <div className="hidden md:block absolute top-8 left-0 w-full h-0.5 bg-slate-200 -z-10 transform translate-y-4"></div>

            {[
              { step: "01", title: "Post", desc: "Create your internship/job offer and build your company profile." },
              { step: "02", title: "Match", desc: "Receive an instant list of AI-matched, motivated candidates." },
              { step: "03", title: "Hire", desc: "Interview the best profiles and make your selection." },
              { step: "04", title: "Onboard", desc: "We handle the cultural pre-training and follow-up." }
            ].map((item, i) => (
              <div key={i} className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 relative">
                <div className="text-5xl font-bold text-slate-100 absolute top-4 right-4">{item.step}</div>
                <div className="h-10 w-10 rounded-full bg-red-600 text-white flex items-center justify-center font-bold mb-6 relative z-10">
                  {i + 1}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Difference */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 text-center">It Doesn't End with the Hire</h2>
            <p className="text-lg text-slate-600 mb-12 text-center">
              A successful hire is all about successful integration. Our candidates are not 'just students'; they are prepared professionals.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 mt-1">
                    <CheckCircle2 className="h-8 w-8 text-red-600" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-slate-900 mb-2">For the Candidate</h4>
                    <p className="text-slate-600 leading-relaxed">Comprehensive training on Japanese business etiquette (e.g., hō-ren-sō), workplace expectations, and culture. Includes a 1-month post-arrival follow-up.</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 mt-1">
                    <CheckCircle2 className="h-8 w-8 text-red-600" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-slate-900 mb-2">For Your Company</h4>
                    <p className="text-slate-600 leading-relaxed">You receive a better-prepared, more autonomous, and quickly operational team member. This dramatically reduces HR burden and increases retention.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Success Story */}
      <section data-testid="success-story-section" className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge variant="outline" className="mb-4 border-red-200 text-red-700 bg-red-50">Success Story</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Crossing Borders to Japan</h2>
            <p className="text-lg text-slate-600">
              Discover how a French engineering intern turned a 6-month placement in Japan into a life-changing experience.
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="relative h-72 lg:h-auto">
                  <img 
                    src={thomasPortrait} 
                    alt="Thomas, engineering intern from Lyon" 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent lg:bg-gradient-to-r"></div>
                  <div className="absolute bottom-4 left-4 lg:bottom-6 lg:left-6">
                    <p className="text-white font-bold text-xl">Thomas</p>
                    <p className="text-white/80 text-sm">Software Engineering Intern from Lyon</p>
                  </div>
                </div>
                <div className="p-8 lg:p-10 flex flex-col justify-center">
                  <blockquote className="text-lg text-slate-700 italic leading-relaxed mb-6 border-l-4 border-red-500 pl-4">
                    "Through this internship, I've learned the importance of teamwork and how to solve problems collaboratively. I'm truly glad that I chose to come to Japan."
                  </blockquote>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-3">
                      <GraduationCap className="h-5 w-5 text-red-500 flex-shrink-0" />
                      <span className="text-slate-600">Engineering student from Lyon, France</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Briefcase className="h-5 w-5 text-red-500 flex-shrink-0" />
                      <span className="text-slate-600">iOS development intern at a Japanese startup</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Globe className="h-5 w-5 text-red-500 flex-shrink-0" />
                      <span className="text-slate-600">Chose Japan out of curiosity for its culture</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-slate-100 bg-slate-50 p-6 lg:p-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="flex gap-4 items-start">
                    <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0">
                      <img src={thomasExhibition} alt="Thomas at exhibition" className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">Real-World Impact</p>
                      <p className="text-slate-500 text-sm">Built an iOS app and presented at exhibitions</p>
                    </div>
                  </div>
                  <div className="flex gap-4 items-start">
                    <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0">
                      <img src={thomasFushimi} alt="Fushimi Inari Taisha" className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">Cultural Discovery</p>
                      <p className="text-slate-500 text-sm">Explored Kyoto, Fushimi Inari, and more</p>
                    </div>
                  </div>
                  <div className="flex gap-4 items-center justify-start md:justify-end">
                    <a 
                      href="https://medium.com/@CareCube_/crossing-borders-to-japan-thomass-journey-as-an-engineering-intern-d4e31a2848f3" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      data-testid="read-full-story-link"
                      className="inline-flex items-center gap-2 text-red-600 font-semibold hover:text-red-700 transition-colors group"
                    >
                      Read Full Story
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12 max-w-6xl mx-auto">
            {[
              {
                name: "Grégoire",
                role: "Engineering Exchange Student",
                school: "ICAM Strasbourg-Europe",
                location: "Shibaura Institute of Technology, Tokyo",
                quote: "Japanese universities offer opportunities to explore cutting-edge science, robotics, and AI. My advice: don't wait, just go for it!",
                img: gregoireJapan,
                link: "https://www.icam.fr/temoignage/gregoire-temoignage-dun-echange-academique-au-japon/"
              },
              {
                name: "Diego",
                role: "Data Analyst Intern",
                school: "UCLy / ESTRI Lyon",
                location: "MSC Japan, Tokyo",
                quote: "I was able to put my language and intercultural courses into practice in a real professional context. I came back more autonomous and adaptable.",
                img: diegoTokyo,
                link: "https://www.ucly.fr/lience-lea-le-stage-de-diego-a-tokyo/"
              },
              {
                name: "Yoann",
                role: "Embedded Systems Intern & Filmmaker",
                school: "CESI Toulouse",
                location: "IoT Startup, Shibuya, Tokyo",
                quote: "Traveling alone to the other side of the world is destabilizing, but these experiences are the ones that allow you to truly grow.",
                img: yoannJapan,
                link: "https://www.cesi.fr/en/testimonials/yugen-international-internship-japan/"
              },
              {
                name: "Mirabelle",
                role: "Chemical Engineering Intern",
                school: "CPE Lyon",
                location: "Air Liquide Laboratories, Tsukuba",
                quote: "Going abroad is the best choice I could have made! It gives you perspective on your life and what you thought was universal.",
                img: mirabelleJapan,
                link: "https://www.cpe.fr/article/aa-international-partir-cgp-eti-stages-et-annee-de-cesure-temoignages-annees-de-cesure-mirabelle-denavit-japon/"
              }
            ].map((story, i) => (
              <a 
                key={i}
                href={story.link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`story-card-${story.name.toLowerCase()}`}
                className="group bg-white rounded-xl shadow-md border border-slate-100 overflow-hidden hover:shadow-xl hover:border-red-200 transition-all duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={story.img} 
                    alt={story.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent"></div>
                  <div className="absolute bottom-3 left-4">
                    <p className="text-white font-bold text-lg">{story.name}</p>
                    <p className="text-white/80 text-xs">{story.school}</p>
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Briefcase className="h-4 w-4 text-red-500 flex-shrink-0" />
                    <span className="text-sm font-medium text-slate-700">{story.role}</span>
                  </div>
                  <p className="text-sm text-slate-500 mb-3">{story.location}</p>
                  <blockquote className="text-sm text-slate-600 italic leading-relaxed border-l-2 border-red-300 pl-3">
                    "{story.quote}"
                  </blockquote>
                  <div className="mt-4 flex items-center gap-1 text-red-600 text-sm font-semibold group-hover:gap-2 transition-all">
                    Read Story <ArrowRight className="h-3.5 w-3.5" />
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Business Models */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Zero Risk, High Flexibility</h2>
            <p className="text-lg text-slate-600">Choose the plan that fits your company's strategy. No complex fees, no hidden costs.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card 
              className="border-2 border-slate-200 hover:border-red-600 transition-colors cursor-pointer group"
              onClick={() => handleDemoClick("Annual Subscription Inquiry")}
            >
              <CardHeader>
                <CardTitle className="text-2xl group-hover:text-red-600 transition-colors">Annual Subscription</CardTitle>
                <CardDescription>Best for continuous recruitment needs</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle2 className="h-5 w-5 text-green-500" /> Basic, Standard, Enterprise plans
                  </li>
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle2 className="h-5 w-5 text-green-500" /> montly job postings
                  </li>
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle2 className="h-5 w-5 text-green-500" /> Dedicated account manager
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card 
              className="border-2 border-red-600 bg-white shadow-xl relative overflow-hidden cursor-pointer"
              onClick={() => handleDemoClick("Success Fee Inquiry")}
            >
              <div className="absolute top-0 right-0 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">POPULAR</div>
              <CardHeader>
                <CardTitle className="text-2xl text-red-600">Success-Based Fee</CardTitle>
                <CardDescription>Pay-per-hire model</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle2 className="h-5 w-5 text-green-500" /> No upfront cost
                  </li>
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle2 className="h-5 w-5 text-green-500" /> Pay only upon successful recruitment
                  </li>
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle2 className="h-5 w-5 text-green-500" /> Risk-free trial period
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">FAQ</h2>
          
          <Tabs defaultValue="About Internjin in General" className="space-y-8">
            <div className="flex justify-center overflow-x-auto pb-4 scrollbar-hide">
              <TabsList className="bg-slate-100 p-1 rounded-full border border-slate-200 h-auto inline-flex">
                {[
                  "General",
                  "Students",
                  "Business Model",
                  "Practical Steps"
                ].map((cat) => (
                  <TabsTrigger 
                    key={cat} 
                    value={cat}
                    className="rounded-full px-4 py-2 text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-red-600 data-[state=active]:shadow-sm text-slate-600 transition-all"
                  >
                    {cat.replace("About ", "")}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

              {[
                {
                  category: "General",
                  items: [
                    {
                      q: "Is Internjin a recruitment agency?",
                      a: "No. Internjin is a matching platform dedicated exclusively to internships, with an educational support component. The goal is training and intercultural exchange, not permanent placement."
                    },
                    {
                      q: "How long has Internjin existed?",
                      a: "Internjin is a startup in its launch phase, founded by two French students based on their own internship experiences in Japan."
                    },
                    {
                      q: "Are you supported by French institutions?",
                      a: "Internjin works in connection with French higher education institutions and is actively seeking institutional partnerships. The model is fully aligned with French legal requirements for internships."
                    }
                  ]
                },
                {
                  category: "Students",
                  items: [
                    {
                      q: "What level are the students you offer?",
                      a: "Students come from French business schools, engineering schools, universities and specialized institutions, from Bachelor to Master level, in tech, design, marketing, finance, HR, and more."
                    },
                    {
                      q: "Do they speak Japanese?",
                      a: "Some do, but it is not mandatory. Students able to work in English are also selected. Applications can be filtered by language criteria, and all students receive cultural preparation about Japan before departure."
                    },
                    {
                      q: "Will the student come to Japan alone?",
                      a: "Yes. The student manages their own travel and accommodation, with Internjin's help beforehand. Internjin can also connect companies with local resources to facilitate the student's reception and integration."
                    },
                    {
                      q: "What happens if the internship does not go well?",
                      a: "Internjin acts as a mediator and intervenes quickly to find a satisfactory solution for both parties. In extreme cases, an early termination of the internship can be arranged according to the terms of the agreement."
                    },
                    {
                      q: "How do French students handle Japanese work culture?",
                      a: "French students tend to be direct and proactive, which may differ from Japanese norms. All students receive intercultural preparation covering communication styles, hierarchy, punctuality and professional etiquette. Internjin remains available throughout the internship to support both parties."
                    },
                    {
                      q: "What if the student has an accident or falls ill in Japan?",
                      a: "The student must have personal health and travel insurance arranged before departure. The company is not responsible for personal health coverage. Workplace accidents follow the responsibilities outlined in the internship agreement, and Internjin can advise both parties."
                    },
                    {
                      q: "Can the student be hired permanently after the internship?",
                      a: "Absolutely, and it happens. If both parties are interested, the company can offer a full-time position at the end of the internship. Internjin fully supports this outcome."
                    }
                  ]
                },
                {
                  category: "Business Model",
                  items: [
                    {
                      q: "How much does it cost for the company?",
                      a: "There is no legal obligation to pay the student under Japanese law, but offering a stipend is strongly recommended to attract the best candidates. Service fees are also paid to Internjin for matching and follow-up; a detailed pricing grid is provided at the introductory meeting."
                    },
                    {
                      q: "Is the student an employee of the company?",
                      a: "No. The student is governed by an internship agreement, no employment contract is required. The internship agreement is the only legal document needed."
                    },
                    {
                      q: "What is the minimum and maximum duration of an internship?",
                      a: "Internships range from 2 months to 1 year, depending on the student's availability, the company's needs and the scope of the project."
                    }
                  ]
                },
                {
                  category: "Practical Steps",
                  items: [
                    {
                      q: "What steps does the company need to take?",
                      a: "Define your needs and desired profiles, sign the internship agreement, designate an internal mentor and welcome the student. Internjin handles candidate selection, document management and ongoing support."
                    },
                    {
                      q: "How long does the matching process take?",
                      a: "Selection and matching generally take 2 to 4 weeks from the moment the offer is published. Internjin recommends planning at least 6 to 8 weeks before the desired start date to account for visa delays and academic calendars."
                    },
                    {
                      q: "Can the internship be extended or renewed if it goes well?",
                      a: "Yes, as long as it remains within the student's academic framework and both parties agree. Any extension must be formalized by an amendment to the original internship agreement, which Internjin will manage."
                    },
                    {
                      q: "Does the company need to speak French?",
                      a: "No. Internjin provides bilingual mediation, and all documents can be provided in English or Japanese."
                    },
                    {
                      q: "How do I start a partnership with Internjin?",
                      a: "Simply contact us to schedule a no-obligation discovery call. The team will develop a proposal tailored to the company's needs."
                    }
                  ]
                }
              ].map((category) => (
              <TabsContent key={category.category} value={category.category} className="animate-in fade-in-50 slide-in-from-bottom-2 duration-300">
                <Accordion type="single" collapsible className="w-full space-y-4">
                  {category.items.map((item, j) => (
                    <AccordionItem key={j} value={`item-${j}`} className="border rounded-xl px-6 bg-slate-50 hover:bg-slate-100 transition-colors border-slate-100 shadow-sm data-[state=open]:bg-white data-[state=open]:shadow-md data-[state=open]:border-red-100">
                      <AccordionTrigger className="text-left font-semibold text-slate-800 hover:text-red-600 hover:no-underline py-4 text-lg">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-600 leading-relaxed pb-6 text-base">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Founders */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Meet the Founders</h2>
            <p className="text-lg text-slate-600">
              Built by former interns in Japan who understand the journey.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* P. Theotime */}
            <Card className="border-none shadow-lg bg-slate-50 hover:shadow-xl transition-shadow">
              <CardContent className="p-8 flex flex-col items-center text-center">
                <div className="h-24 w-24 bg-slate-200 rounded-full flex items-center justify-center mb-6 text-2xl font-bold text-slate-600 border-4 border-white shadow-sm">
                  PT
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-1">P. Theotime</h3>
                <p className="text-red-600 font-medium mb-6">Co-Founder</p>
                
                <div className="space-y-4 text-slate-600 w-full">
                  <div className="flex items-start gap-3 text-left bg-white p-4 rounded-lg border border-slate-100">
                    <GraduationCap className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-slate-900">Master in Robotics Engineering</p>
                      <p className="text-sm text-slate-500">CPE Lyon, France</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 text-left bg-white p-4 rounded-lg border border-slate-100">
                    <Briefcase className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-slate-900">6-month Internship</p>
                      <p className="text-sm text-slate-500">BMW Japan (Tokyo)</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* D. Chahan */}
            <Card className="border-none shadow-lg bg-slate-50 hover:shadow-xl transition-shadow">
              <CardContent className="p-8 flex flex-col items-center text-center">
                <div className="h-24 w-24 bg-slate-200 rounded-full flex items-center justify-center mb-6 text-2xl font-bold text-slate-600 border-4 border-white shadow-sm">
                  DC
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-1">D. Chahan</h3>
                <p className="text-red-600 font-medium mb-6">Co-Founder</p>
                
                <div className="space-y-4 text-slate-600 w-full">
                  <div className="flex items-start gap-3 text-left bg-white p-4 rounded-lg border border-slate-100">
                    <GraduationCap className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-slate-900">Master in Robotics Engineering</p>
                      <p className="text-sm text-slate-500">CPE Lyon, France</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 text-left bg-white p-4 rounded-lg border border-slate-100">
                    <Briefcase className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-slate-900">6-month Internship</p>
                      <p className="text-sm text-slate-500">Sound & Vision Technologies (Osaka)</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Next Steps */}
      <section className="py-24 bg-slate-900 text-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none">
           <div className="absolute -top-24 -right-24 w-96 h-96 bg-red-600 rounded-full blur-3xl"></div>
           <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-600 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge variant="outline" className="mb-4 border-red-500 text-red-400">Future Roadmap</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Extending the Network to All Europe</h2>
            <p className="text-xl text-slate-300 font-light">
              Our mission doesn't stop at France. We are building the ultimate bridge between Japanese companies and Europe's most prestigious universities.
            </p>
            <div className="relative w-full max-w-5xl mx-auto mt-16 aspect-[16/9] rounded-xl overflow-hidden bg-slate-50 border border-slate-200 shadow-xl group">
              <img 
                src={mapWorld} 
                alt="World Map" 
                className="w-full h-full object-cover object-center"
              />
            </div>

            {/* East Asia Expansion */}
            <div className="mt-24 text-center">
              <Badge variant="outline" className="mb-4 border-blue-500 text-blue-400">Phase 2</Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Expanding to East Asia</h2>
              <p className="text-xl text-slate-300 font-light max-w-3xl mx-auto mb-12">
                Our vision includes bridging Europe with the vibrant economies of South Korea, Malaysia, Singapore, and China.
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { name: "South Korea", img: flagKorea },
                  { name: "Malaysia", img: flagMalaysia },
                  { name: "Singapore", img: flagSingapore },
                  { name: "China", img: flagChina }
                ].map((country, i) => (
                  <div key={i} className="group relative rounded-xl overflow-hidden aspect-square border border-slate-700 bg-slate-800 shadow-lg hover:shadow-blue-500/20 hover:border-blue-500/50 transition-all duration-300">
                    <img src={country.img} alt={country.name} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-slate-900 via-slate-900/80 to-transparent p-4">
                      <p className="font-bold text-white text-lg">{country.name}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Home;
